import { useEffect, useMemo, useState } from "react"

import { loadSourceArtifactText } from "@/admin/monitoring/company-run-research-data"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { errorMessage } from "@/lib/error-message"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"

type StoredObject = {
  privateObjectPath: string
  label: string
  mimeType: string
  byteCount: number
}

// Past this size a viewer would stall the page, so it shows no text.
const textPreviewByteLimit = 5_000_000

const isTextPreviewable = ({ mimeType, byteCount }: StoredObject) =>
  byteCount <= textPreviewByteLimit &&
  (mimeType.startsWith("text/") || /[/+](json|xml)$/.test(mimeType))

// Stored objects are content-addressed, so a path's text never changes and
// reopening one, or stepping back to its manifest, reads it once.
const textByPath = new Map<string, Promise<string>>()
const readStoredText = (privateObjectPath: string) => {
  let text = textByPath.get(privateObjectPath)
  if (!text) {
    text = loadSourceArtifactText({ data: { privateObjectPath } })
    textByPath.set(privateObjectPath, text)
    text.catch(() => textByPath.delete(privateObjectPath))
  }
  return text
}

// The page objects a manifest names (ADR-0106, `SourcePageReference`), each
// labelled by the URL it was read from or, for a portal stage, its name.
const pageReferences = (value: unknown, key = ""): StoredObject[] => {
  if (typeof value !== "object" || value === null) return []
  if (Array.isArray(value))
    return value.flatMap((item) => pageReferences(item, key))
  const record = value as Record<string, unknown>
  const {
    private_object_path: path,
    mime_type: mime,
    byte_count: size,
  } = record
  if (typeof path === "string")
    return typeof mime === "string" && typeof size === "number"
      ? [
          {
            privateObjectPath: path,
            label: key,
            mimeType: mime,
            byteCount: size,
          },
        ]
      : []
  return Object.entries(record).flatMap(([childKey, child]) =>
    pageReferences(
      child,
      typeof record.url === "string" ? record.url : childKey
    )
  )
}

const parseJson = (text: string) => {
  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

const ObjectContent = ({
  stored,
  text,
  onOpenPage,
}: {
  stored: StoredObject
  text: string
  onOpenPage: (page: StoredObject) => void
}) => {
  const json = useMemo(() => {
    const parsed = /json$/.test(stored.mimeType) ? parseJson(text) : undefined
    return parsed === undefined
      ? null
      : {
          pages: pageReferences(parsed),
          pretty: JSON.stringify(parsed, null, 2),
        }
  }, [stored.mimeType, text])
  const source = (
    <pre className="bg-surface-sunken overflow-auto p-3 text-xs break-all whitespace-pre-wrap">
      {json?.pretty ?? text}
    </pre>
  )
  if (stored.mimeType === "text/html")
    return (
      <Tabs defaultValue="rendered" className="flex min-h-0 flex-1 flex-col">
        <TabsList>
          <TabsTrigger value="rendered">Rendered</TabsTrigger>
          <TabsTrigger value="source">Source</TabsTrigger>
        </TabsList>
        <TabsContent value="rendered" className="min-h-0 flex-1">
          {/* An empty sandbox runs no script and reaches no cookie. */}
          <iframe
            title="Stored HTML page"
            sandbox=""
            srcDoc={text}
            className="h-[60vh] w-full border bg-white"
          />
        </TabsContent>
        <TabsContent value="source" className="min-h-0 flex-1 overflow-auto">
          {source}
        </TabsContent>
      </Tabs>
    )
  if (!json?.pages.length) return source
  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-auto">
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Stored pages</h3>
        <ul className="space-y-1 text-xs">
          {json.pages.map((page, index) => (
            <li key={`${page.privateObjectPath}-${index}`}>
              <button
                type="button"
                className="text-left break-all underline-offset-4 hover:underline"
                onClick={() => onOpenPage(page)}
              >
                {page.label || page.privateObjectPath}
              </button>
              <span className="text-muted-foreground"> {page.mimeType}</span>
            </li>
          ))}
        </ul>
      </section>
      {source}
    </div>
  )
}

// A Source Artifact as the private bucket stores it, opened from its manifest
// into the pages it names.
export const SourceArtifactViewer = ({
  artifact,
  triggerLabel,
}: {
  artifact: StoredObject
  triggerLabel: string
}) => {
  const { theme } = useTerminalTheme()
  const [trail, setTrail] = useState<StoredObject[]>([])
  const [text, setText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const current = trail.at(-1)
  const isPreviewable = current !== undefined && isTextPreviewable(current)
  useEffect(() => {
    if (!current || !isPreviewable) return
    let isCancelled = false
    setText(null)
    setError(null)
    readStoredText(current.privateObjectPath)
      .then((loaded) => {
        if (!isCancelled) setText(loaded)
      })
      .catch((cause: unknown) => {
        if (!isCancelled) setError(errorMessage(cause, "Read failed"))
      })
    return () => {
      isCancelled = true
    }
  }, [current, isPreviewable])
  return (
    <>
      <Button
        variant="link"
        size="sm"
        className="h-auto px-0"
        onClick={() => setTrail([artifact])}
      >
        {triggerLabel}
      </Button>
      <Dialog
        open={current !== undefined}
        onOpenChange={(isOpen) => {
          if (!isOpen) setTrail([])
        }}
      >
        <DialogContent
          data-terminal-theme={theme}
          className="monitoring-workspace bg-surface-layer text-text-primary font-mono sm:max-h-[90vh] sm:max-w-5xl"
        >
          <DialogHeader className="pr-8">
            <DialogTitle className="text-sm break-all">
              {current?.label}
            </DialogTitle>
            <DialogDescription className="text-xs break-all">
              {current?.privateObjectPath}
            </DialogDescription>
          </DialogHeader>
          {trail.length > 1 && (
            <Button
              variant="link"
              size="sm"
              className="h-auto self-start px-0"
              onClick={() => setTrail(trail.slice(0, -1))}
            >
              Back to {trail.at(-2)!.label}
            </Button>
          )}
          {error && (
            <p role="alert" className="text-assessment-critical text-sm">
              {error}
            </p>
          )}
          {current && !isPreviewable && (
            <p className="text-muted-foreground text-sm">
              No text preview for this {current.mimeType} object of{" "}
              {current.byteCount.toLocaleString()} bytes.
            </p>
          )}
          {isPreviewable && !error && text === null && (
            <p role="status" className="text-muted-foreground text-sm">
              Reading from storage…
            </p>
          )}
          {current && isPreviewable && text !== null && (
            <ObjectContent
              stored={current}
              text={text}
              onOpenPage={(page) => setTrail([...trail, page])}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
